import { useEffect, useState } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'

interface MinHeight {
  minHeight: number
}

export default function useAppBarHeight (): number {
  const {
    mixins: { toolbar },
    breakpoints
  } = useTheme()
  const toolbarDesktopQuery = breakpoints.up('sm')
  const toolbarLandscapeQuery = `${breakpoints.up('xs')} and (orientation: landscape)`
  const isDesktop = useMediaQuery(toolbarDesktopQuery)
  const isLandscape = useMediaQuery(toolbarLandscapeQuery)
  let currentToolbarMinHeight
  if (isDesktop) {
    currentToolbarMinHeight = toolbar[toolbarDesktopQuery]
  } else if (isLandscape) {
    currentToolbarMinHeight = toolbar[toolbarLandscapeQuery]
  } else {
    currentToolbarMinHeight = toolbar
  }
  const fallbackHeight = (currentToolbarMinHeight as MinHeight)?.minHeight ?? 0
  const [appBarHeight, setAppBarHeight] = useState<number>(fallbackHeight)

  // Prefer measured height when the app bar exists (multi-row toolbars, zoom); fall back to theme toolbar minHeight
  useEffect(() => {
    const customAppBar = document.querySelector<HTMLElement>('[data-custom-app-bar="true"]')

    if (customAppBar == null) {
      setAppBarHeight(fallbackHeight)
      return
    }

    const updateAppBarHeight = (): void => {
      setAppBarHeight(customAppBar.getBoundingClientRect().height || fallbackHeight)
    }

    updateAppBarHeight()

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
        updateAppBarHeight()
      })
      : null

    resizeObserver?.observe(customAppBar)
    window.addEventListener('resize', updateAppBarHeight)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateAppBarHeight)
    }
  }, [fallbackHeight])

  return appBarHeight
}
